import React from 'react'
import { Box, Typography } from '@mui/material'

import { INTERAX_COLORS } from '../../../constants/colors'
import { Button } from '../../../interaxDesignSystemComponents'
import useAppState from '../../../hooks/useAppState'
import useFirebaseUser from '../../../hooks/useFirebaseUser'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'

interface ProfileAccountInformationSectionProps {
  isDevMode: boolean
  onTitleClick: () => void
  onEditClick: () => void
}

/**
 * Profile card: avatar, display name, email, phone, user id, edit button.
 * The section title is the secret tap target for unlocking dev mode.
 */
export const ProfileAccountInformationSection: React.FC<ProfileAccountInformationSectionProps> = ({
  isDevMode,
  onTitleClick,
  onEditClick
}) => {
  const { t } = useTranslation()
  const { state } = useAppState()
  const { data: firebaseUser } = useFirebaseUser()

  if (!firebaseUser && !state.user) return null

  const displayName = state.user?.displayName || firebaseUser?.displayName
  const email = state.user?.email || firebaseUser?.email
  const phone = state.user?.phone
  const userId = state.user?.id || firebaseUser?.uid
  const initial = displayName?.charAt(0) || email?.charAt(0) || 'F'

  return (
    <Box
      sx={{
        padding: '24px',
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '12px',
        marginBottom: '24px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}
      >
        <Typography
          variant="h5"
          onClick={onTitleClick}
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: INTERAX_COLORS.white,
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          {t('facilitator.profile.accountInformation')}
          {isDevMode && (
            <Typography
              component="span"
              sx={{
                marginLeft: '12px',
                fontSize: '12px',
                padding: '2px 8px',
                backgroundColor: INTERAX_COLORS.sun,
                color: INTERAX_COLORS.offBlack,
                borderRadius: '4px',
                fontWeight: 600
              }}
            >
              {t('facilitator.trainingSprint.profile.devModeActive')}
            </Typography>
          )}
        </Typography>
        <Button variant="secondary" onClick={onEditClick}>
          {t('facilitator.profile.editAccountInformation')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Box
          sx={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${INTERAX_COLORS.heritageBlue}, ${INTERAX_COLORS.sun})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              color: INTERAX_COLORS.white
            }}
          >
            {initial}
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: INTERAX_COLORS.white,
              marginBottom: '4px'
            }}
          >
            {displayName || t('facilitator.profile.facilitator')}
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              color: INTERAX_COLORS.thunderGrey,
              marginBottom: '4px'
            }}
          >
            {email}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: INTERAX_COLORS.thunderGrey,
              marginBottom: '4px'
            }}
          >
            {t('facilitator.profile.phoneNumber')}: {phone || t('facilitator.profile.noPhoneNumber')}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.thunderGrey }}>
            {t('facilitator.profile.userId')} {userId}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
